import { useEffect, useState } from 'react';
import { SubmitHandler, useForm, Controller } from 'react-hook-form';
import { HttpStatusCode } from 'axios';
import useApi from '../../../utils/AxiosWrapper';
import { ClipLoader } from 'react-spinners';
import { useUser } from '../../../utils/UserContext';
import SignupFooter from '../SignupFooter';
import { Select, Radio } from 'antd';
import dayjs from 'dayjs';
import { AddressComponents } from '../../../utils/AddressUtils';
import { rejectIncompleteAddress } from '../../../utils/incompleteAddressGuard';
import {
  ApplicationStatus,
  ApplicationStep,
  CreateDriverRequest,
  Entity,
} from '../../../types/apiTypes';
import { useScreenView } from '../../../utils/ScreenViewContext';
import { US_STATES } from '../../../types/constants';
import { setApplicationStep } from '../../../pages/signup';
import { Application } from '../../../types/dbTypes';
import toast from 'react-hot-toast';
import { useAuth0 } from '@auth0/auth0-react';
import SensitiveFieldHelp from '../SensitiveFieldHelp';
import {
  formatUSPhoneForDisplay,
  formatUSPhonePretty,
  parseUSPhoneToE164,
} from '../../../utils/phone';
import MaskedDatePicker from './MaskedDatePicker';
import TrustSignals from '../TrustSignals';
import useFrontChat from '../../../hooks/useFrontChat';
import FlexibleAddressInput from './FlexibleAddressInput';
import { normalizeStoredPhone } from '../../../utils/carrierPrefill';
import {
  dateOfBirthPickerMonth,
  getDateOfBirthError,
} from '../../../utils/DateUtils';
import { PERSONAL_INFO_STORAGE_KEY } from '../../../constants/headsUp';
import { DATE_OF_BIRTH_MESSAGES } from '../../../constants/dateOfBirth';
import {
  MAX_OWNERSHIP_PERCENTAGE,
  MIN_OWNERSHIP_PERCENTAGE,
  OWNERSHIP_PERCENTAGE_MESSAGES,
} from '../../../constants/ownership';
import {
  safeSessionGetItem,
  safeSessionSetItem,
} from '../../../shared/safeStorage';

interface CustomerInfoProps {
  currStep: string;
  selectedRole: Entity;
  setStep: (step: string) => void;
  application: Application;
}
/**
 * Type definition for personal form data
 * Contains fields for:
 * - Basic info (name, email, phone)
 * - Date of birth
 * - Address details (street number, name, unit, city, state, zip)
 * - SSN
 */
export type PersonalFormData = {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  dateOfBirth: string;
  address: string;
  address_st_number?: string;
  address_st_name?: string;
  address_unit?: string;
  address_city?: string;
  address_state?: string;
  address_zip?: string;
  ssn: string;
  businessOwnershipPercentage: string;
  otherBeneficialOwners: boolean;
  driversLicenseNumber: string;
  driversLicenseState: string;
};

function AccountInfo({
  currStep,
  selectedRole,
  setStep,
  application,
}: CustomerInfoProps) {
  // Initialize Front chat widget
  useFrontChat();

  const { callProtectedApi } = useApi();
  const {
    register,
    handleSubmit,
    setValue,
    setError,
    clearErrors,
    watch,
    control,
    formState: { errors },
  } = useForm<PersonalFormData>({
    defaultValues: {
      businessOwnershipPercentage: `${MAX_OWNERSHIP_PERCENTAGE}%`,
    },
  });
  const { userRecord, userId } = useUser();
  const [loading, setLoading] = useState(false);
  const { screenView } = useScreenView();

  const { user } = useAuth0();

  /* eslint-disable @typescript-eslint/no-unused-vars */
  const [pageLoading, setPageLoading] = useState(false);
  // Removed addressFields state
  const [addressSelectedFromDropdown, setAddressSelectedFromDropdown] =
    useState(false);

  const onSubmit: SubmitHandler<PersonalFormData> = (data) => {
    const e164 = parseUSPhoneToE164(data.phoneNumber);
    if (!e164) {
      toast.error('Please enter a valid US phone number.');
      return;
    }
    data.phoneNumber = e164;
    // The last check before the API: whichever way the address got into the form (a Places pick,
    // the manual fields, or a restored session), the request only goes out with every part the
    // backend requires. `upsertPrimaryDriverRecord` is only reachable from here.
    if (rejectIncompleteAddress(data, setError, 'address')) return;
    // E.164 to the API, the formatted number to the session copy the form and Review read back —
    // see the same split in BusinessInfo.
    safeSessionSetItem(
      PERSONAL_INFO_STORAGE_KEY,
      JSON.stringify({ ...data, phoneNumber: formatUSPhonePretty(e164) })
    );
    upsertPrimaryDriverRecord(data, userId ?? '');
    // incrementStep();
  };

  const businessOwnershipPercentage = watch('businessOwnershipPercentage');

  const driversLicenseState = watch('driversLicenseState');
  const addressState = watch('address_state');

  const upsertPrimaryDriverRecord = async (
    customerData: PersonalFormData,
    customer_id: string
  ) => {
    setLoading(true);
    try {
      const normalizedAddressFields: AddressComponents = {
        address_st_number: customerData.address_st_number,
        address_st_name: customerData.address_st_name,
        address_unit: customerData.address_unit,
        address_city: customerData.address_city,
        address_state: customerData.address_state,
        address_zip: customerData.address_zip,
      };

      const response = await callProtectedApi(
        `/drivers/primary`,
        'post',
        {
          ...normalizedAddressFields,
          // address line 2 (unit) is a nullable column — omit it when empty (the backend stores
          // null) instead of sending a "-" placeholder.
          address_unit:
            normalizedAddressFields.address_unit?.trim() || undefined,
          customer_id: customer_id,
          first_name: customerData?.firstName,
          last_name: customerData?.lastName,
          phone_number: customerData?.phoneNumber,
          email: customerData?.email,
          ssn: customerData?.ssn,
          date_of_birth: new Date(customerData?.dateOfBirth ?? ''),
          is_primary_owner: true,
          business_ownership_percentage: Number(
            customerData?.businessOwnershipPercentage
          ),
          drivers_license_number: customerData?.driversLicenseNumber,
          drivers_license_state: customerData?.driversLicenseState,
        } as CreateDriverRequest,
        'Error occurred while updating your carrier driver record.'
      );
      const data = response?.data?.data?.driver;
      if (
        (data &&
          Object.entries(data)?.length > 0 &&
          response?.status === HttpStatusCode.Created) ||
        response?.status === HttpStatusCode.Ok
      ) {
        if (customerData?.otherBeneficialOwners) {
          const success = await patchCustomer(
            customerData?.otherBeneficialOwners,
            customer_id
          );
          if (!success) {
            toast.error('Error occurred while updating your business info.');
            return;
          }
        }

        // Business Debt step removed from the flow — PersonalInfo now advances to Choose Tier
        // (THT-773). The Highway carrier-details workflow no longer runs on this transition —
        // it fires on the Choose Tier → Review submit instead, which is also where a rejected
        // MC/DOT now bounces the customer back to Business Info.
        const step = await setApplicationStep({
          step: ApplicationStep.ChoosePlan,
          application_id: application?.application_id,
          customer_id: userId ?? '',
          callProtectedApi,
          setLoading: handleSetLoading,
          previousStep: ApplicationStep.PersonalInfo,
          applicationStatus: application?.status as ApplicationStatus,
        });
        if (step) {
          setStep(step);
        }
      }
    } catch (e: any) {
      console.error('Error occurred while creating carrier driver record: ', e);
    } finally {
      setLoading(false);
    }
  };

  const patchCustomer = async (
    otherBeneficialOwners: boolean,
    customer_id: string
  ) => {
    setLoading(true);
    try {
      const response = await callProtectedApi(
        `/customers/${customer_id}`,
        'patch',
        {
          other_beneficial_owners: otherBeneficialOwners,
        },
        'Error occurred while updating your business info.'
      );
      setLoading(false);
      if (response?.status === HttpStatusCode.Ok) {
        return true;
      }
      return false;
    } catch (e: any) {
      setLoading(false);
      console.error(
        "Error occurred while updating customer's business info: ",
        e
      );
      return false;
    }
  };

  const handleSetLoading = (loading: boolean) => {
    setLoading(loading);
  };

  const formatSSN = (value: string) => {
    // Remove all non-digit characters
    const numbersOnly = value.replace(/\D/g, '');
    // Limit to a max of 9 digits
    const ssn = numbersOnly.slice(0, 9);

    // Format as XXX-XX-XXXX
    const formattedSSN = ssn.replace(
      /^(\d{3})(\d{0,2})(\d{0,4})$/,
      (match, p1, p2, p3) => {
        if (p3) return `${p1}-${p2}-${p3}`;
        else if (p2) return `${p1}-${p2}`;
        else if (p1) return `${p1}`;
        return '';
      }
    );

    return formattedSSN;
  };

  // Prefill driver's license state with the address state if available
  useEffect(() => {
    if (addressState && !driversLicenseState) {
      setValue('driversLicenseState', addressState);
    }
  }, [addressState, driversLicenseState, setValue]);

  // Modify the existing useEffect for session storage
  useEffect(() => {
    // Retrieve form data from session storage
    setValue('email', user?.email || '');
    // The account's phone is stored E.164; the field shows it the way this form writes phones.
    setValue('phoneNumber', formatUSPhoneForDisplay(user?.phone_number || ''));
    const savedData = safeSessionGetItem(PERSONAL_INFO_STORAGE_KEY);
    if (savedData) {
      const parsedData: PersonalFormData = JSON.parse(savedData);

      if (parsedData.dateOfBirth) {
        const dob = dayjs(parsedData.dateOfBirth, [
          'MMMM Do, YYYY',
          'MMMM D, YYYY',
          'YYYY-MM-DD',
        ]);

        if (dob.isValid()) {
          parsedData.dateOfBirth = dob.format('YYYY-MM-DD');
        }
      }

      // Shown the way this form writes phones, whatever shape the session happens to hold —
      // see the same normalisation in BusinessInfo.
      if (parsedData.phoneNumber) {
        parsedData.phoneNumber = normalizeStoredPhone(parsedData.phoneNumber);
      }

      // Set form data using setValue
      (Object.keys(parsedData) as (keyof PersonalFormData)[]).forEach((key) => {
        setValue(key, parsedData[key]);
      });

      if (parsedData?.address_city?.length) {
        setAddressSelectedFromDropdown(true);
      }
    }
  }, [setValue, user]);

  const renderDriversLicenseFields = () => (
    <>
      <div className="flex w-full flex-col items-center gap-6 align-middle">
        <div className="flex w-full flex-col items-center gap-6 md:flex-row md:gap-4">
          <div className="w-full md:w-[50%]">
            <label
              htmlFor="driversLicenseState"
              className={`${screenView === 'desktop' ? 'flex items-center' : 'block'} text-sm font-medium text-gray-700`}
            >
              Driver's License State
              {screenView === 'desktop' && (
                <SensitiveFieldHelp fieldType="DRIVERS_LICENSE_STATE" />
              )}
            </label>
            <Controller
              name="driversLicenseState"
              control={control}
              rules={{ required: "Driver's license state is required" }}
              render={({ field }) => (
                <Select
                  {...field}
                  id="driversLicenseState"
                  className={`mt-1 w-full ${errors.driversLicenseState ? 'ant-select-error' : ''}`}
                  placeholder="Select a state"
                  style={{
                    height: '38px',
                  }}
                  status={errors.driversLicenseState ? 'error' : undefined}
                  options={US_STATES.map((state) => ({
                    value: state.abbreviation,
                    label: state.name,
                  }))}
                />
              )}
            />
            {errors.driversLicenseState && (
              <p className="mt-1 text-sm text-red-500">
                {errors.driversLicenseState.message}
              </p>
            )}
            {screenView !== 'desktop' && (
              <SensitiveFieldHelp fieldType="DRIVERS_LICENSE_STATE" />
            )}
          </div>

          <div className="w-full md:w-[50%]">
            <label
              htmlFor="driversLicenseNumber"
              className={`${screenView === 'desktop' ? 'flex items-center' : 'block'} text-sm font-medium text-gray-700`}
            >
              Driver's License Number
              {screenView === 'desktop' && (
                <SensitiveFieldHelp fieldType="DRIVERS_LICENSE_NUMBER" />
              )}
            </label>
            <input
              type="text"
              placeholder="DL12345678"
              {...register('driversLicenseNumber', {
                required: "Driver's license number is required",
              })}
              id="driversLicenseNumber"
              className={`mt-1 block w-full border px-3 py-2 ${
                errors.driversLicenseNumber
                  ? 'border-red-500'
                  : 'border-gray-300'
              } focus:border-blue-400 focus:outline-none focus:ring-blue-400 sm:text-sm`}
            />
            {errors.driversLicenseNumber && (
              <p className="mt-1 text-sm text-red-500">
                {errors.driversLicenseNumber.message}
              </p>
            )}
            {screenView !== 'desktop' && (
              <SensitiveFieldHelp fieldType="DRIVERS_LICENSE_NUMBER" />
            )}
          </div>
        </div>

        <div className="flex w-full flex-col items-center gap-4 md:flex-row">
          <div className="w-full">
            <label
              htmlFor="businessOwnershipPercentage"
              className={`${screenView === 'desktop' ? 'flex items-center' : 'block'} text-sm font-medium text-gray-700`}
            >
              What percentage of the business do you own?
              {screenView === 'desktop' && (
                <SensitiveFieldHelp fieldType="OWNERSHIP_PERCENTAGE" />
              )}
            </label>
            <input
              type="text"
              placeholder={`${MAX_OWNERSHIP_PERCENTAGE}%`}
              {...register('businessOwnershipPercentage', {
                required: OWNERSHIP_PERCENTAGE_MESSAGES.required,
                validate: (value) => {
                  const numValue =
                    typeof value === 'string'
                      ? parseInt(value.replace(/[^0-9]/g, ''), 10)
                      : value;
                  if (isNaN(numValue))
                    return OWNERSHIP_PERCENTAGE_MESSAGES.invalid;
                  // THT-758: Sila only registers beneficial owners at or above
                  // the floor, so anything under it is stopped here rather than
                  // failing verification days into onboarding.
                  if (numValue < MIN_OWNERSHIP_PERCENTAGE)
                    return OWNERSHIP_PERCENTAGE_MESSAGES.belowMinimum;
                  if (numValue > MAX_OWNERSHIP_PERCENTAGE)
                    return OWNERSHIP_PERCENTAGE_MESSAGES.aboveMaximum;
                  return true;
                },
                onChange: (e) => {
                  // Only allow numbers and % sign
                  const value = e.target.value;
                  const filtered = value.replace(/[^0-9%]/g, '');

                  // Ensure only one % sign at the end
                  const withoutPercent = filtered.replace(/%/g, '');
                  const hasPercent = filtered.includes('%');

                  if (withoutPercent === '') {
                    // Allow empty field
                    e.target.value = '';
                  } else if (hasPercent) {
                    e.target.value = withoutPercent + '%';
                  } else {
                    e.target.value = withoutPercent;
                  }
                },
                onBlur: (e) => {
                  // Format with % on blur only if there's a value
                  const value = e.target.value.replace(/%/g, '');
                  if (value !== '') {
                    const numValue = parseInt(value, 10);
                    if (!isNaN(numValue)) {
                      // Only clamp DOWN to the ceiling — never raise a low
                      // answer up to the floor, which would silently overwrite
                      // what the customer told us (THT-758).
                      const limitedValue = Math.min(
                        numValue,
                        MAX_OWNERSHIP_PERCENTAGE
                      );
                      e.target.value = `${limitedValue}%`;
                    }
                  }
                  // If empty, leave it empty
                },
                setValueAs: (value) => {
                  // Strip the % for form submission
                  if (typeof value === 'string') {
                    const cleanValue = value.replace(/%/g, '');
                    return cleanValue === '' ? '' : parseInt(cleanValue, 10);
                  }
                  return typeof value === 'number' ? value : '';
                },
              })}
              id="businessOwnershipPercentage"
              className={`mt-1 block w-full border px-3 py-2 ${
                errors.businessOwnershipPercentage
                  ? 'border-red-500'
                  : 'border-gray-300'
              } focus:border-blue-400 focus:outline-none focus:ring-blue-400 sm:text-sm`}
            />
            {errors.businessOwnershipPercentage && (
              <p className="mt-1 text-sm text-red-500">
                {errors.businessOwnershipPercentage.message}
              </p>
            )}
            {screenView !== 'desktop' && (
              <SensitiveFieldHelp fieldType="OWNERSHIP_PERCENTAGE" />
            )}
          </div>
        </div>
        {Number(businessOwnershipPercentage) <= 75 && (
          <div className="w-full">
            <label
              htmlFor="otherBeneficialOwners"
              className="block text-sm font-medium text-gray-700"
            >
              Are there any other individuals who own 25% or more of the
              company?
            </label>
            <Controller
              name="otherBeneficialOwners"
              control={control}
              rules={{
                validate: (value: boolean | undefined) =>
                  value !== undefined || 'Please select an option',
              }}
              render={({ field }) => (
                <Radio.Group
                  value={field.value}
                  onChange={(e) => field.onChange(e.target.value)}
                  id="otherBeneficialOwners"
                  className={`mt-1 flex h-[38px] items-center ${errors.otherBeneficialOwners ? 'ant-radio-group-error' : ''}`}
                >
                  <Radio value={true}>Yes</Radio>
                  <Radio value={false}>No</Radio>
                </Radio.Group>
              )}
            />
            {errors.otherBeneficialOwners && (
              <p className="mt-1 text-sm text-red-500">
                {errors.otherBeneficialOwners.message}
              </p>
            )}
          </div>
        )}
      </div>
    </>
  );

  const renderScreenView = (screenView: string) => {
    switch (screenView) {
      case 'desktop':
      case 'tablet':
      case 'mobile':
        return (
          <div className="flex h-full w-full flex-col items-center">
            {!loading ? (
              <>
                <div className="w-full flex-1 overflow-y-auto py-10 lg:overflow-visible lg:py-20">
                  <div className="flex w-full justify-center px-6 lg:px-12">
                    <div className="mx-auto flex w-auto flex-col items-center justify-center gap-8 lg:flex-row">
                      <div className="flex w-full items-center justify-center lg:px-12">
                        <div className="mx-auto flex w-auto flex-col items-center justify-center gap-8 lg:flex-row lg:items-start">
                          {/* Main form content */}
                          <div className="mx-auto flex-1 lg:w-[800px] lg:max-w-[800px]">
                            <div className="mx-auto mb-6 max-w-[600px] text-center lg:mb-8 lg:max-w-full lg:text-start">
                              <h2 className="text-[30px] font-bold">
                                Owner Info
                              </h2>
                              <p className="text-gray-500">
                                A few details about yourself. We will need
                                information about all beneficial owners with 25%
                                or more ownership.
                              </p>
                            </div>

                            {userRecord &&
                            userRecord?.userType === Entity.Customer &&
                            userRecord?.customer_id?.length ? (
                              <form
                                onSubmit={handleSubmit(onSubmit)}
                                className="mx-auto max-w-[600px] space-y-6 lg:max-w-full"
                              >
                                <div className="flex w-full flex-col items-center gap-6 align-middle md:flex-row md:gap-4">
                                  <div className="w-full md:w-[50%]">
                                    <label
                                      htmlFor="firstName"
                                      className="block text-sm font-medium text-gray-700"
                                    >
                                      First Name
                                    </label>
                                    <input
                                      type="text"
                                      placeholder="John"
                                      {...register('firstName', {
                                        required: 'First name is required',
                                      })}
                                      id="firstName"
                                      className={`mt-1 block w-full border px-3 py-2 ${errors.firstName ? 'border-red-500' : 'border-gray-300'} focus:border-blue-400 focus:outline-none focus:ring-blue-400 sm:text-sm`}
                                    />
                                    {errors.firstName && (
                                      <p className="mt-1 text-sm text-red-500">
                                        {errors.firstName.message}
                                      </p>
                                    )}
                                  </div>

                                  <div className="w-full md:w-[50%]">
                                    <label
                                      htmlFor="lastName"
                                      className="block text-sm font-medium text-gray-700"
                                    >
                                      Last Name
                                    </label>
                                    <input
                                      type="text"
                                      placeholder="Doe"
                                      {...register('lastName', {
                                        required: 'Last name is required',
                                      })}
                                      id="lastName"
                                      className={`mt-1 block w-full border px-3 py-2 ${errors.lastName ? 'border-red-500' : 'border-gray-300'} focus:border-blue-400 focus:outline-none focus:ring-blue-400 sm:text-sm`}
                                    />
                                    {errors.lastName && (
                                      <p className="mt-1 text-sm text-red-500">
                                        {errors.lastName.message}
                                      </p>
                                    )}
                                  </div>
                                </div>
                                <div className="flex w-full flex-col items-center gap-6 align-middle md:flex-row md:gap-4">
                                  <div className="w-full md:w-[50%]">
                                    <label
                                      htmlFor="email"
                                      className="block text-sm font-medium text-gray-700"
                                    >
                                      Email
                                    </label>
                                    <input
                                      type="email"
                                      placeholder="johndoe@example.com"
                                      {...register('email', {
                                        required: 'Email is required',
                                        pattern: {
                                          value: /^\S+@\S+$/i,
                                          message: 'Invalid email address',
                                        },
                                      })}
                                      id="email"
                                      className={`mt-1 block w-full border px-3 py-2 ${errors.email ? 'border-red-500' : 'border-gray-300'} focus:border-blue-400 focus:outline-none focus:ring-blue-400 sm:text-sm`}
                                    />
                                    {errors.email && (
                                      <p className="mt-1 text-sm text-red-500">
                                        {errors.email.message}
                                      </p>
                                    )}
                                  </div>

                                  <div className="w-full md:w-[50%]">
                                    <label
                                      htmlFor="phoneNumber"
                                      className="block text-sm font-medium text-gray-700"
                                    >
                                      Phone Number
                                    </label>

                                    <input
                                      placeholder="4042223333"
                                      type="tel"
                                      {...register('phoneNumber', {
                                        required: 'Phone number is required',
                                        validate: (value) =>
                                          parseUSPhoneToE164(value)
                                            ? true
                                            : 'Enter a valid US phone number',
                                      })}
                                      onChange={(e) => {
                                        // Format as the user types. Only validate once the number is
                                        // actually valid so we don't show (or leave stale) an
                                        // "invalid" message while they're still typing.
                                        const formatted = formatUSPhonePretty(
                                          e.target.value
                                        );
                                        const isValid =
                                          !!parseUSPhoneToE164(formatted);
                                        setValue('phoneNumber', formatted, {
                                          shouldValidate: isValid,
                                        });
                                        if (!isValid) {
                                          clearErrors('phoneNumber');
                                        }
                                      }}
                                      id="phoneNumber"
                                      className={`mt-1 block w-full border px-3 py-2 ${errors.phoneNumber ? 'border-red-500' : 'border-gray-300'} focus:border-blue-400 focus:outline-none focus:ring-blue-400 sm:text-sm`}
                                    />
                                    {errors.phoneNumber && (
                                      <p className="mt-1 text-sm text-red-500">
                                        {errors.phoneNumber.message}
                                      </p>
                                    )}
                                  </div>
                                </div>
                                <div className="relative w-full">
                                  <FlexibleAddressInput
                                    label="Address"
                                    searchFieldName="address"
                                    streetNumberFieldName="address_st_number"
                                    streetNameFieldName="address_st_name"
                                    unitFieldName="address_unit"
                                    cityFieldName="address_city"
                                    stateFieldName="address_state"
                                    zipFieldName="address_zip"
                                    register={register}
                                    control={control}
                                    watch={watch}
                                    setValue={setValue}
                                    errors={errors}
                                    initialMode="autocomplete"
                                    requireCompleteAddress
                                    searchPlaceholder="Enter Address"
                                    addressSelectedFromDropdown={
                                      addressSelectedFromDropdown
                                    }
                                    setAddressSelectedFromDropdown={
                                      setAddressSelectedFromDropdown
                                    }
                                    cookieKey="addressMode"
                                  />
                                </div>

                                <div className="flex w-full flex-col items-center gap-6 align-middle md:flex-row md:gap-4">
                                  <div className="w-full md:w-[50%]">
                                    <label
                                      htmlFor="dateOfBirth"
                                      className="block text-sm font-medium text-gray-700"
                                    >
                                      Date of Birth
                                    </label>
                                    <Controller
                                      name="dateOfBirth"
                                      control={control}
                                      rules={{
                                        required:
                                          DATE_OF_BIRTH_MESSAGES.required,
                                        validate: (value) =>
                                          getDateOfBirthError(value) ?? true,
                                      }}
                                      render={({ field }) => (
                                        <MaskedDatePicker
                                          id="dateOfBirth"
                                          errors={errors}
                                          defaultPickerValue={dateOfBirthPickerMonth(
                                            field.value
                                          )}
                                          value={
                                            field.value
                                              ? dayjs(field.value)
                                              : null
                                          } // keep ISO in form
                                          onChange={(d: any) => {
                                            // setValue rather than field.onChange, the same shape
                                            // the phone and SSN fields use above: only setValue can
                                            // commit a value WITHOUT scheduling validation.
                                            // field.onChange always runs RHF's change pipeline,
                                            // which re-validates on every keystroke once the form
                                            // has been submitted once — so a half-typed date would
                                            // render "Date of birth is required" mid-edit.
                                            setValue(
                                              'dateOfBirth',
                                              d ? d.format('YYYY-MM-DD') : '',
                                              // Validate as soon as a COMPLETE date lands. The rest
                                              // of the form waits for submit, but an off-by-a-decade
                                              // birth year is not a mistake anyone re-reads unless
                                              // they are told.
                                              { shouldValidate: !!d }
                                            );
                                            // Clearing or backspacing must take the message with it,
                                            // or an age error stays rendered under an empty field.
                                            if (!d) clearErrors('dateOfBirth');
                                          }}
                                          status={
                                            errors.dateOfBirth
                                              ? 'error'
                                              : undefined
                                          }
                                        />
                                      )}
                                    />
                                    {errors.dateOfBirth && (
                                      <p className="mt-1 text-sm text-red-500">
                                        {errors.dateOfBirth.message}
                                      </p>
                                    )}
                                  </div>

                                  <div className="w-full md:w-[50%]">
                                    <label
                                      htmlFor="ssn"
                                      className={`${screenView === 'desktop' ? 'flex items-center' : 'block'} text-sm font-medium text-gray-700`}
                                    >
                                      Social Security Number
                                      {screenView === 'desktop' && (
                                        <SensitiveFieldHelp fieldType="SSN" />
                                      )}
                                    </label>
                                    <input
                                      type="text"
                                      placeholder="XXX-XX-XXXX"
                                      {...register('ssn', {
                                        required: 'SSN is required',
                                        pattern: {
                                          value:
                                            /^(?!666|000|9\d{2})\d{3}-(?!00)\d{2}-(?!0{4})\d{4}$/,
                                          message: 'Please enter a valid SSN',
                                        },
                                      })}
                                      id="ssn"
                                      className={`mt-1 block w-full border px-3 py-2 ${errors.ssn ? 'border-red-500' : 'border-gray-300'} focus:border-blue-400 focus:outline-none focus:ring-blue-400 sm:text-sm`}
                                      onChange={(e) => {
                                        const formattedValue = formatSSN(
                                          e.target.value
                                        );
                                        // Only validate once a full SSN (XXX-XX-XXXX) is entered so
                                        // we don't flag "invalid" while the user is still typing.
                                        const isComplete =
                                          formattedValue.length === 11;
                                        setValue('ssn', formattedValue, {
                                          shouldValidate: isComplete,
                                        });
                                        if (!isComplete) {
                                          clearErrors('ssn');
                                        }
                                      }}
                                    />
                                    {errors.ssn && (
                                      <p className="mt-1 text-sm text-red-500">
                                        {errors.ssn.message}
                                      </p>
                                    )}
                                    {screenView !== 'desktop' && (
                                      <SensitiveFieldHelp fieldType="SSN" />
                                    )}
                                  </div>
                                </div>

                                {renderDriversLicenseFields()}

                                <SignupFooter
                                  selectedRole={selectedRole}
                                  currStep={currStep}
                                  onBackClick={() => {
                                    setStep('business_info');
                                  }}
                                  isMobile={screenView === 'mobile'}
                                />
                              </form>
                            ) : (
                              <ClipLoader
                                color="#1D213B"
                                loading={pageLoading}
                                size={60}
                                aria-label="Loading Spinner"
                                data-testid="loader"
                                className="h-full w-full justify-center"
                              />
                            )}
                          </div>
                          {/* Desktop sidebar with trust signals */}
                          {screenView === 'desktop' ? (
                            <div className="sticky top-8 hidden h-fit w-[350px] lg:block">
                              <TrustSignals variant="sidebar" />
                            </div>
                          ) : (
                            <div className="mb-4 w-full max-w-[600px]">
                              <TrustSignals variant="mobile" />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex h-full w-full items-center justify-center">
                <ClipLoader
                  color="#1D213B"
                  loading={loading}
                  size={60}
                  aria-label="Loading Spinner"
                  data-testid="loader"
                  className="h-full w-full items-center justify-center pt-10"
                />
              </div>
            )}
          </div>
        );
    }
  };

  return <>{renderScreenView(screenView)}</>;
}

export default AccountInfo;
